import React from 'react';

import { useTheme } from '../../theme/ThemeContext';
import DatePicker from './DatePicker';

/**
 * Shared "how long is this advice valid" control for advising a procedure or
 * lab test. Renders a date (default +30d, set by the caller) labelled "Valid
 * until", plus a "No expiry — standing order" toggle. When standing, the date
 * becomes the review date the repeatable order auto-expires on.
 *
 * The standing option is disabled (with the admin's reason) when the picked
 * catalog item is flagged `one_time_only`.
 *
 * Controlled: parent owns `{ expiresAt, standing }` and gets the merged patch
 * via `onChange({ expiresAt, standing })`.
 */
export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function plusDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ValidityControl({
  expiresAt,
  standing = false,
  oneTimeOnly = false,
  oneTimeOnlyReason,
  onChange,
}) {
  const { isDark } = useTheme();
  const set = (patch) => onChange({ expiresAt: expiresAt || '', standing, ...patch });

  const muted = isDark ? '#94a3b8' : '#64748b';
  const labelStyle = { fontSize: '0.6875rem', opacity: 0.8, display: 'block', marginBottom: '0.2rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <div>
        <label style={labelStyle}>{standing ? 'Review date' : 'Valid until'}</label>
        <DatePicker
          value={expiresAt || ''}
          min={todayISO()}
          isDark={isDark}
          onChange={(iso) => set({ expiresAt: iso })}
        />
      </div>

      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          fontSize: '0.75rem',
          color: oneTimeOnly ? muted : (isDark ? '#e2e8f0' : '#1f2937'),
          cursor: oneTimeOnly ? 'not-allowed' : 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={standing}
          disabled={oneTimeOnly}
          onChange={(e) => set({ standing: e.target.checked })}
        />
        No expiry — standing (repeatable) order
      </label>

      {standing && !oneTimeOnly && (
        <p style={{ margin: 0, fontSize: '0.6875rem', color: muted }}>
          Stays on the patient and can be performed again and again until the review date above.
        </p>
      )}
      {oneTimeOnly && (
        <p style={{ margin: 0, fontSize: '0.6875rem', color: isDark ? '#fca5a5' : '#b91c1c' }}>
          {oneTimeOnlyReason || 'This item must be done once — a standing order isn’t allowed.'}
        </p>
      )}
    </div>
  );
}
